/**
 * Credits: the unit a customer is sold, and the only unit they are ever shown.
 *
 * ## Why not sell calls, and why not sell tokens
 *
 * The allowance used to be a call count, which is the one unit that is definitely wrong. A Luna wand
 * cast and a high-quality enhance differ by roughly 330× in what they cost to serve, and both
 * counted as 1 against the same monthly number. That makes the cheap features feel expensive, lets
 * the expensive ones run unchecked, and gives the operator a figure that cannot be reasoned about.
 *
 * Tokens are the other obvious unit and are worse, for a different reason: they are a true
 * measurement, and they are meaningless to the person paying. Nobody knows whether 40,000 tokens is
 * a lot. **No token count is ever shown in the UI.**
 *
 * So: credits. Coarse, stable, comparable across features, and expressible in the terms someone
 * actually thinks in — "about ten full site reconstructions".
 *
 * ## One credit ≈ $0.04 of estimated provider cost, rounded up, minimum 1
 *
 * A *pricing* unit, not an accounting one. The accounting unit is `estimated_cost_usd`, which the
 * ledger records beside the credits charged precisely so margin per feature stays a single query.
 * The anchor was chosen so the tier table in `docs/PRICING_IMPLEMENTATION_PLAN.md` comes out right,
 * and the rounding is deliberately in the operator's favour on every individual call: the minimum
 * of 1 means the cheapest possible call still costs something, and the ceiling means nothing is
 * ever served below its own cost.
 *
 * ## Reserve before, charge after
 *
 * An image call can be priced exactly before it is made — size, quality and count are in the
 * request. A Responses call cannot: what it costs depends on how many tokens come back, and that is
 * known only afterwards. So a call is *quoted* from a table of typical usage, refused if the quote
 * does not fit in the remaining balance, and then charged what it actually came to.
 *
 * That ordering is the point. The previous code counted calls already made and let the current one
 * through regardless, so the wall always landed one call later than it should have — which on a
 * five-stage reconstruction meant the balance could go past zero by a whole stage.
 */

import { AI_OPERATION_SPECS, type AiOperation } from './models.js';
import { estimateImageCallCostUsd, estimateTokenCostUsd, type TokenUsage } from './pricing.js';

/** Estimated provider cost that one credit is priced to cover. */
export const CREDIT_USD = 0.04;

/**
 * Credits for a known dollar cost: ceiling, never below 1.
 *
 * The epsilon is not decoration. `0.08 / 0.04` is `2.0000000000000004` in binary floating point, and
 * without it an exactly-two-credit call would be charged three. Every price in the table is a two-
 * or three-decimal figure being divided by another, so this case is common rather than exotic.
 */
export function creditsForUsd(usd: number): number {
  if (!Number.isFinite(usd) || usd <= 0) return 1;
  return Math.max(1, Math.ceil(usd / CREDIT_USD - 1e-9));
}

/**
 * What a call of each kind typically consumes, for quoting it before it is made.
 *
 * Stated as token counts rather than as dollars so the quote responds to the model actually named:
 * the same wand cast on Luna and on Sol differ by 18× in cost and should not be quoted the same
 * number. It also means a change to the price table moves the quotes with it, which is the property
 * that stops the two drifting.
 *
 * These are estimates seeded from the plan document, and they are expected to be somewhat wrong.
 * They are only ever used to *reserve*; what is charged is what the call actually cost. Being
 * moderately wrong here costs a user a slightly early or slightly late wall, not a wrong bill.
 */
export const TYPICAL_TOKEN_USAGE: Record<AiOperation, TokenUsage> = {
  // One photograph plus a short instruction, and a handful of tags back.
  'tag-suggest': { inputTokens: 4_000, cachedTokens: 0, outputTokens: 1_000 },
  // One stage of five: the site photograph, the plan accumulated so far, and a structured answer.
  'site-plan': { inputTokens: 10_000, cachedTokens: 0, outputTokens: 3_000 },
  // A selection and an instruction in, a placement plan out.
  wand: { inputTokens: 6_000, cachedTokens: 0, outputTokens: 1_500 },
  // Larger output than the wand: a full material recipe, with web search results in the input.
  forge: { inputTokens: 8_000, cachedTokens: 0, outputTokens: 2_800 },
  // Unused — priced from the request instead. Present because the record must be total.
  swatch: { inputTokens: 0, cachedTokens: 0, outputTokens: 0 },
  enhance: { inputTokens: 0, cachedTokens: 0, outputTokens: 0 },
};

/**
 * What an image call is assumed to be when the request did not say.
 *
 * The swatch endpoint sends a size but no quality and lets the provider choose, so an unquoted
 * image call is quoted as one medium square — `normaliseQuality` in `./pricing.js` makes the same
 * assumption on the recording side, for the same reason and with the same caveat.
 */
const ASSUMED_IMAGE_SIZE = '1024x1024';

/** What an image request asked for, as the server read it off the wire. */
export interface ImageRequestShape {
  size: string | null;
  quality: string | null;
  count: number;
}

/**
 * Credits to reserve before making a call.
 *
 * Never returns null. An unpriced model — someone typing into the reconstruction dialog's "Other"
 * box — is quoted as though it were the operation's default, which is the only defensible guess:
 * quoting it at zero would make an unknown model the cheapest way to use the app.
 */
export function reserveCreditsFor(
  operation: AiOperation,
  model: string,
  image: ImageRequestShape | null = null,
): number {
  const spec = AI_OPERATION_SPECS[operation];

  if (spec.metering === 'images') {
    const asked = image?.count;
    const count = typeof asked === 'number' && Number.isFinite(asked) ? Math.max(1, asked) : 1;
    const usd =
      estimateImageCallCostUsd(
        model,
        image?.size ?? ASSUMED_IMAGE_SIZE,
        image?.quality ?? null,
        count,
      ) ?? estimateImageCallCostUsd(spec.defaultModel, ASSUMED_IMAGE_SIZE, null, count);
    return creditsForUsd(usd ?? CREDIT_USD);
  }

  const usage = TYPICAL_TOKEN_USAGE[operation];
  const usd = estimateTokenCostUsd(model, usage) ?? estimateTokenCostUsd(spec.defaultModel, usage);
  return creditsForUsd(usd ?? CREDIT_USD);
}

/**
 * Credits actually charged, once the call is done.
 *
 * `estimatedCostUsd` is null when the model has no price on file, and the reservation stands in for
 * it. That is the honest fallback: the cost column keeps its NULL, which is what makes the monthly
 * reconciliation trustworthy, while the user is still charged the figure they were quoted rather
 * than nothing at all.
 */
export function creditsCharged(estimatedCostUsd: number | null, reserved: number): number {
  if (estimatedCostUsd === null) return Math.max(1, reserved);
  return creditsForUsd(estimatedCostUsd);
}

// ---------------------------------------------------------------------------
// Saying what a balance is worth, in something other than credits
// ---------------------------------------------------------------------------

/**
 * Credits for one complete site reconstruction, derived rather than asserted.
 *
 * A reconstruction is not one call: `SitePipelineRunner` makes one upstream call per stage across
 * five stages, and around them sit the tagging pre-pass and the forge and swatch calls the surfaces
 * stage spawns. Computing the total from the same table that prices the parts means the headline
 * number in the UI cannot fall out of step with what the pipeline is actually charged.
 */
export const RECONSTRUCTION_CREDITS =
  reserveCreditsFor('tag-suggest', AI_OPERATION_SPECS['tag-suggest'].defaultModel) +
  5 * reserveCreditsFor('site-plan', AI_OPERATION_SPECS['site-plan'].defaultModel) +
  reserveCreditsFor('forge', AI_OPERATION_SPECS.forge.defaultModel) +
  reserveCreditsFor('swatch', AI_OPERATION_SPECS.swatch.defaultModel);

/** Credits for one enhanced render at the top quality tier — the other end of the range. */
export const HIGH_ENHANCE_CREDITS = reserveCreditsFor(
  'enhance',
  AI_OPERATION_SPECS.enhance.defaultModel,
  { size: '1536x1024', quality: 'high', count: 1 },
);

/**
 * A balance in the terms someone actually thinks in.
 *
 * Two anchors rather than one, and deliberately the two ends of the range: quoting only
 * reconstructions would make the allowance look tiny, and quoting only enhanced renders would make
 * it look limitless. Together they say the true thing, which is that it depends what you do with it.
 */
/**
 * When this month's allowance comes back, as a date someone can put in a diary.
 *
 * Derived from the server's own `period` rather than from the browser's clock. The allowance rolls
 * on `date_trunc('month', now())` *in the database's timezone*, so a client west of it computing
 * "first of next month" locally would name the wrong day for several hours at each month boundary —
 * which is exactly when a user who has just run out is looking at this number.
 *
 * Falls back to the browser's month only when no period is known yet, which is the one case where
 * being approximately right beats saying nothing.
 */
export function creditsResetLabel(period?: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period ?? '');
  const now = new Date();
  const year = match ? Number(match[1]) : now.getUTCFullYear();
  // `period` is 1-based; the Date constructor's month is 0-based, so an unadjusted value already
  // names the *next* month — which is the one we want.
  const month = match ? Number(match[2]) : now.getUTCMonth() + 1;
  const reset = new Date(Date.UTC(year, month, 1));
  return `${String(reset.getUTCDate())} ${MONTH_NAMES[reset.getUTCMonth()]}`;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function describeCredits(credits: number): string {
  if (credits <= 0) return 'none left this month';

  const reconstructions = Math.floor(credits / RECONSTRUCTION_CREDITS);
  const enhances = Math.floor(credits / HIGH_ENHANCE_CREDITS);

  if (reconstructions < 1) {
    return enhances < 1
      ? 'enough for a few more wand casts'
      : `about ${String(enhances)} enhanced render${enhances === 1 ? '' : 's'}`;
  }
  return (
    `about ${String(reconstructions)} full site reconstruction${reconstructions === 1 ? '' : 's'}, ` +
    `or ${String(enhances)} high-quality enhanced renders`
  );
}
